import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  Copy,
  Eye,
  FolderKanban,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { ExportButton } from "@/components/ui/ExportButton";
import { ColumnSelectorButton } from "@/components/ui/ColumnSelectorButton";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  useColumnVisibility,
  type ColumnDef,
} from "@/hooks/useColumnVisibility";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useDecisionList } from "@/modules/decisions/hooks/useDecisions";
import { useIdeaList } from "@/modules/ideas/hooks/useIdeas";
import { useMilestoneList } from "@/modules/milestones/hooks/useMilestones";
import {
  useDeleteTask,
  useTaskList,
  useUpdateTaskStatus,
} from "../hooks/useTasks";
import { NewTaskDrawer } from "../components/NewTaskDrawer";
import { TaskStatusBadge } from "../components/TaskStatusBadge";
import { TaskPriorityBadge } from "../components/TaskPriorityBadge";
import type { TaskListItem, TaskStatus } from "../types";

const BORDER = "hsl(210,30%,85%)";

function initialsOf(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n) + "…" : s;
}

const TASK_COLUMNS: ColumnDef[] = [
  { key: "code", label: "#" },
  { key: "title", label: "Title" },
  { key: "projectName", label: "Project" },
  { key: "phaseName", label: "Phase", defaultVisible: false },
  { key: "priority", label: "Priority" },
  { key: "status", label: "Status" },
  { key: "dueDate", label: "Due Date" },
  { key: "assigneeName", label: "Assignee" },
];

const STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  { value: "OPEN", label: "Open" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "IN_REVIEW", label: "In Review" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
];

const iconBtnBase =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-transparent text-muted-foreground transition-[background,color] duration-150 hover:bg-muted hover:text-foreground";
const iconBtnDelete =
  "inline-flex h-7 w-7 items-center justify-center rounded-md bg-transparent text-muted-foreground transition-[background,color] duration-150 hover:bg-[hsl(0,70%,95%)] hover:text-[hsl(0,84%,50%)]";

export default function TaskListPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [decisionFilter, setDecisionFilter] = useState("");
  const [ideaFilter, setIdeaFilter] = useState("");
  const [milestoneFilter, setMilestoneFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState<TaskListItem | null>(
    null,
  );
  const [toast, setToast] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [statusPopover, setStatusPopover] = useState<string | null>(null);
  const [popoverError, setPopoverError] = useState<string | null>(null);

  const {
    visibility,
    toggle: toggleColumn,
    reset: resetColumns,
    visibleKeys,
    isVisible,
    minReached,
  } = useColumnVisibility("tasks", TASK_COLUMNS);
  const exportFilters = useMemo(
    () => ({
      search: search || undefined,
      status: statusFilter || undefined,
      priority: priorityFilter || undefined,
      projectId: projectFilter || undefined,
      decisionId: decisionFilter || undefined,
      ideaId: ideaFilter || undefined,
      milestoneId: milestoneFilter || undefined,
    }),
    [
      search,
      statusFilter,
      priorityFilter,
      projectFilter,
      decisionFilter,
      ideaFilter,
      milestoneFilter,
    ],
  );

  const { data, isLoading, isError, error } = useTaskList({
    page,
    limit: pageSize,
    search: search || undefined,
    status: statusFilter || undefined,
    priority: priorityFilter || undefined,
    projectId: projectFilter || undefined,
    decisionId: decisionFilter || undefined,
    ideaId: ideaFilter || undefined,
    milestoneId: milestoneFilter || undefined,
  });
  const { data: projectsResult } = useProjectList({ page: 1, limit: 200 });
  const projects = projectsResult?.data ?? [];
  const { data: decisionsResult } = useDecisionList({ page: 1, limit: 100 });
  const decisions = decisionsResult?.data ?? [];
  const { data: ideasResult } = useIdeaList({ page: 1, limit: 100 });
  const ideas = ideasResult?.data ?? [];
  const { data: milestonesResult } = useMilestoneList({ page: 1, limit: 100 });
  const milestones = milestonesResult?.data ?? [];

  const updateStatus = useUpdateTaskStatus();
  const deleteTask = useDeleteTask();

  const tasks = data?.data ?? [];
  const total = data?.pagination.total ?? 0;
  const totalPages = Math.max(1, data?.pagination.totalPages ?? 1);
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  function resetPage<T>(setter: (v: T) => void) {
    return (value: T) => {
      setter(value);
      setPage(1);
    };
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  async function handleStatusChange(taskId: string, newStatus: TaskStatus) {
    setPopoverError(null);
    const res = await updateStatus.mutateAsync({ id: taskId, status: newStatus });
    if ("incompleteActions" in res) {
      setPopoverError(
        `Cannot complete — ${res.incompleteActions.incompleteCount} action(s) still open`,
      );
      return;
    }
    setStatusPopover(null);
    showToast(`Task status updated to ${newStatus.replace(/_/g, " ")}`);
  }

  function handleDelete(task: TaskListItem) {
    setDeleteTarget({ id: task.id, name: task.title });
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    await deleteTask.mutateAsync(deleteTarget.id);
    showToast("Task deleted");
  }

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Daily Work" },
        { label: "Tasks" },
      ]}
    >
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
          style={{ borderColor: "hsl(142,55%,80%)" }}
        >
          {toast}
        </div>
      )}

      <div className="w-full space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckSquare
              className="h-[18px] w-[18px] text-foreground"
              strokeWidth={1.5}
            />
            <h1 className="text-2xl font-semibold tracking-tight">Tasks</h1>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton
              module="tasks"
              filters={exportFilters}
              visibleColumns={visibleKeys}
            />
            <ColumnSelectorButton
              columns={TASK_COLUMNS}
              visibility={visibility}
              onToggle={toggleColumn}
              onReset={resetColumns}
              minReached={minReached}
            />
            <Button onClick={() => setDrawerOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={1.5} />
              New Task
            </Button>
          </div>
        </div>

        {isError ? (
          <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-6 text-sm text-destructive">
            <div className="font-medium">Failed to load tasks</div>
            <div className="mt-1 font-mono text-xs opacity-80">
              {(error as Error)?.message ?? "Unknown error"}
            </div>
          </div>
        ) : (
          <>
            {/* Filter row — wraps to a second line when filters exceed viewport */}
            <div className="mb-3 flex flex-wrap items-center gap-3">
              <div className="relative w-[280px] shrink-0">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Input
                  placeholder="Search tasks..."
                  value={search}
                  onChange={(e) => resetPage(setSearch)(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select
                value={statusFilter}
                onChange={(e) => resetPage(setStatusFilter)(e.target.value)}
                className="w-40 shrink-0"
              >
                <option value="">All Status</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Select>
              <Select
                value={priorityFilter}
                onChange={(e) => resetPage(setPriorityFilter)(e.target.value)}
                className="w-40 shrink-0"
              >
                <option value="">All Priority</option>
                <option value="CRITICAL">Critical</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </Select>
              <Select
                value={projectFilter}
                onChange={(e) => resetPage(setProjectFilter)(e.target.value)}
                className="w-48 shrink-0"
              >
                <option value="">All Projects</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
              <Select
                value={decisionFilter}
                onChange={(e) => resetPage(setDecisionFilter)(e.target.value)}
                className="w-48 shrink-0"
              >
                <option value="">All Decisions</option>
                {decisions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.code} — {d.title}
                  </option>
                ))}
              </Select>
              <Select
                value={ideaFilter}
                onChange={(e) => resetPage(setIdeaFilter)(e.target.value)}
                className="w-48 shrink-0"
              >
                <option value="">All Ideas</option>
                {ideas.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.code} — {i.title}
                  </option>
                ))}
              </Select>
              <Select
                value={milestoneFilter}
                onChange={(e) => resetPage(setMilestoneFilter)(e.target.value)}
                className="w-48 shrink-0"
              >
                <option value="">All Milestones</option>
                {milestones.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.code} — {m.title}
                  </option>
                ))}
              </Select>
            </div>

            {/* Table card */}
            <div
              className="overflow-hidden rounded-xl border bg-card"
              style={{ borderColor: BORDER }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                  <Loader2
                    className="mr-2 h-4 w-4 animate-spin"
                    strokeWidth={1.5}
                  />
                  Loading tasks…
                </div>
              ) : tasks.length === 0 ? (
                <div className="py-16 text-center text-sm text-muted-foreground">
                  No tasks match the current filters.
                </div>
              ) : (
                <table
                  className="w-full text-[13px]"
                  style={{ borderCollapse: "collapse" }}
                >
                  <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <tr>
                      {isVisible("code") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 120 }}>
                          #
                        </th>
                      )}
                      {isVisible("title") && (
                        <th className="px-[14px] py-[9px]">Title</th>
                      )}
                      {isVisible("projectName") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 150 }}>
                          Project
                        </th>
                      )}
                      {isVisible("phaseName") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 120 }}>
                          Phase
                        </th>
                      )}
                      {isVisible("priority") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 90 }}>
                          Priority
                        </th>
                      )}
                      {isVisible("status") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 110 }}>
                          Status
                        </th>
                      )}
                      {isVisible("dueDate") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 120 }}>
                          Due Date
                        </th>
                      )}
                      {isVisible("assigneeName") && (
                        <th className="px-[14px] py-[9px]" style={{ width: 130 }}>
                          Assignee
                        </th>
                      )}
                      <th
                        className="px-[14px] py-[9px] text-right"
                        style={{ width: 130 }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {tasks.map((t) => {
                      const isOverdue =
                        !!t.dueDate &&
                        new Date(t.dueDate) < new Date() &&
                        t.status !== "COMPLETED" &&
                        t.status !== "CANCELLED";
                      const contextName =
                        t.projectName ?? t.companyName ?? t.productName ?? null;
                      return (
                        <tr
                          key={t.id}
                          className="cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
                          onClick={() => navigate(`/tasks/${t.id}`)}
                        >
                          {isVisible("code") && (
                            <td className="px-[14px] py-[11px] font-mono text-[12px] text-muted-foreground">
                              {t.code}
                            </td>
                          )}
                          {isVisible("title") && (
                            <td className="px-[14px] py-[11px]">
                              <Link
                                to={`/tasks/${t.id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="font-medium text-foreground hover:text-[hsl(210,100%,35%)]"
                              >
                                {truncate(t.title, 50)}
                              </Link>
                            </td>
                          )}
                          {isVisible("projectName") && (
                            <td className="px-[14px] py-[11px] text-muted-foreground">
                              {contextName ? (
                                <span className="inline-flex items-center gap-1.5">
                                  <FolderKanban
                                    className="h-3 w-3"
                                    strokeWidth={1.5}
                                  />
                                  <span className="truncate">{contextName}</span>
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                          )}
                          {isVisible("phaseName") && (
                            <td className="px-[14px] py-[11px]">
                              {t.phaseName ? (
                                <span
                                  className="inline-flex rounded-full px-2 py-0.5 text-[11px] text-muted-foreground"
                                  style={{ background: "hsl(210,16%,95%)" }}
                                >
                                  {t.phaseName}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                          )}
                          {isVisible("priority") && (
                            <td className="px-[14px] py-[11px]">
                              <TaskPriorityBadge priority={t.priority} />
                            </td>
                          )}
                          {isVisible("status") && (
                            <td className="px-[14px] py-[11px]">
                              <TaskStatusBadge status={t.status} />
                            </td>
                          )}
                          {isVisible("dueDate") && (
                            <td className="px-[14px] py-[11px]">
                              {t.dueDate ? (
                                <span className="inline-flex items-center gap-2">
                                  <span
                                    className={
                                      isOverdue
                                        ? "font-medium text-[hsl(0,70%,45%)]"
                                        : "text-foreground"
                                    }
                                  >
                                    {formatDate(t.dueDate)}
                                  </span>
                                  {isOverdue && (
                                    <span
                                      className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
                                      style={{
                                        background: "hsl(0,70%,94%)",
                                        color: "hsl(0,70%,40%)",
                                      }}
                                    >
                                      Overdue
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </td>
                          )}
                          {isVisible("assigneeName") && (
                            <td className="px-[14px] py-[11px]">
                              {t.assignedToName ? (
                                <span className="inline-flex items-center gap-2">
                                  <span
                                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                                    style={{
                                      background: "hsl(210,55%,92%)",
                                      color: "hsl(210,100%,28%)",
                                    }}
                                  >
                                    {initialsOf(t.assignedToName)}
                                  </span>
                                  <span className="text-foreground">
                                    {t.assignedToName}
                                  </span>
                                </span>
                              ) : (
                                <span className="text-muted-foreground">
                                  Unassigned
                                </span>
                              )}
                            </td>
                          )}
                          <td
                            className="relative px-[14px] py-[11px] text-right"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                className={iconBtnBase}
                                title="View"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/tasks/${t.id}`);
                                }}
                              >
                                <Eye
                                  className="h-[15px] w-[15px]"
                                  strokeWidth={1.5}
                                />
                              </button>
                              <button
                                type="button"
                                className={iconBtnBase}
                                title="Edit"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/tasks/${t.id}`);
                                }}
                              >
                                <Pencil
                                  className="h-[15px] w-[15px]"
                                  strokeWidth={1.5}
                                />
                              </button>
                              <button
                                type="button"
                                className={iconBtnBase}
                                title="Duplicate"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDuplicateTarget(t);
                                }}
                              >
                                <Copy
                                  className="h-[15px] w-[15px]"
                                  strokeWidth={1.5}
                                />
                              </button>
                              <button
                                type="button"
                                className={iconBtnBase}
                                title="Change Status"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPopoverError(null);
                                  setStatusPopover(
                                    statusPopover === t.id ? null : t.id,
                                  );
                                }}
                              >
                                <RefreshCw
                                  className="h-[15px] w-[15px]"
                                  strokeWidth={1.5}
                                />
                              </button>
                              <button
                                type="button"
                                className={iconBtnDelete}
                                title="Delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(t);
                                }}
                              >
                                <Trash2
                                  className="h-[15px] w-[15px]"
                                  strokeWidth={1.5}
                                />
                              </button>
                            </div>
                            {statusPopover === t.id && (
                              <div
                                className="absolute right-4 top-10 z-20 w-56 rounded-lg border bg-white p-2 shadow-lg"
                                style={{ borderColor: BORDER }}
                              >
                                {STATUS_OPTIONS.map((opt) => {
                                  const disabled =
                                    opt.value === "COMPLETED" &&
                                    t.incompleteActionsCount > 0;
                                  return (
                                    <button
                                      key={opt.value}
                                      type="button"
                                      disabled={disabled}
                                      onClick={() =>
                                        handleStatusChange(t.id, opt.value)
                                      }
                                      className="block w-full rounded px-3 py-1.5 text-left text-sm text-foreground hover:bg-[hsl(210,50%,96%)] disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                      {opt.label}
                                    </button>
                                  );
                                })}
                                {popoverError && (
                                  <div
                                    className="mt-1 rounded px-3 py-1.5 text-[11px]"
                                    style={{
                                      background: "hsl(0,70%,96%)",
                                      color: "hsl(0,70%,40%)",
                                    }}
                                  >
                                    {popoverError}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {/* Pagination footer */}
              <div
                className="flex items-center justify-between border-t bg-[hsl(210,30%,97%)] px-[14px] py-[9px] text-xs text-muted-foreground"
                style={{ borderColor: BORDER }}
              >
                <div className="flex items-center gap-3">
                  <span>
                    Showing {start}-{end} of {total} entries
                  </span>
                  <span className="flex items-center gap-1.5">
                    Show
                    <Select
                      value={String(pageSize)}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      className="h-7 w-[68px] text-xs"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                    </Select>
                    entries
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <span className="px-2 text-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <NewTaskDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreated={(code) => showToast(`Task created: ${code}`)}
      />

      <NewTaskDrawer
        isOpen={!!duplicateTarget}
        onClose={() => setDuplicateTarget(null)}
        duplicateFrom={duplicateTarget ?? undefined}
        onCreated={(code) => showToast(`Task created: ${code}`)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete task?"
        description={
          deleteTarget
            ? `Are you sure you want to delete '${deleteTarget.name}'? It will be removed from your Tasks list.`
            : ""
        }
        onConfirm={confirmDelete}
      />
    </AppLayout>
  );
}
